import React from "react";
import { motion } from "framer-motion";
import {
  Radio,
  Pencil,
  RefreshCw,
  LayoutGrid,
  Calendar,
  Wallet,
  Send,
  Trash2,
  ArrowRight,
  Check,
} from "lucide-react";
import { railStatus, statusColor } from "@/lib/railStatus";
import StatusPill from "./primitives/StatusPill";

/**
 * PnrCard — ticket-perforation card for a single PNR.
 *
 * Replaces the inline card JSX in pages/Dashboard.jsx. All interactive
 * handlers come from props so the rewritten Dashboard.jsx can pass through
 * its existing axios-backed handlers without changes.
 *
 * Testids preserved verbatim:
 *   pnr-card-{pnr_number}            — outer article
 *   delete-pnr-{pnr_number}          — trash button
 *   edit-passengers-{pnr_number}     — edit button
 *   refresh-pnr-{pnr_number}         — refresh button
 *   seat-chart-{pnr_number}          — grid button
 *   sync-calendar-{pnr_number}       — calendar button
 *   add-wallet-{pnr_number}          — Google Wallet button
 *   add-apple-wallet-{pnr_number}    — Apple Wallet button
 *   share-telegram-{pnr_number}      — send button
 *   live-status-{pnr_number}         — radio button
 *   passenger-{pnr_number}-{idx}     — each passenger row
 *   status-badge-{pnr_number}-{idx}  — each StatusPill
 *
 * The card stays mounted; idx-keyed framer-motion entrance is applied by
 * the parent (Dashboard.jsx) using the `index` prop.
 */

const ActionBtn = ({
  icon,
  label,
  tone = "default",
  onClick,
  disabled,
  testid,
  spinning,
}) => {
  const tones = {
    default: "text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800/60",
    indigo: "text-zinc-400 hover:text-indigo-300 hover:bg-indigo-500/10",
    emerald: "text-zinc-400 hover:text-emerald-300 hover:bg-emerald-500/10",
    sky: "text-zinc-400 hover:text-sky-300 hover:bg-sky-500/10",
    rose: "text-zinc-400 hover:text-rose-300 hover:bg-rose-500/10",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      data-testid={testid}
      className={`w-8 h-8 rounded-md flex items-center justify-center transition-colors ${
        tones[tone]
      } disabled:opacity-40 disabled:hover:bg-transparent ${
        spinning ? "animate-spin" : ""
      }`}
    >
      {icon}
    </button>
  );
};

const SeatMicrograph = ({ passengers }) => {
  const seats = passengers.slice(0, 8).map((p) => railStatus(p.current_status));
  while (seats.length < 8) seats.push("empty");
  return (
    <div className="grid grid-cols-4 gap-[3px] w-[68px]">
      {seats.map((s, i) => (
        <span
          key={i}
          className={`h-2 rounded-sm ${
            s === "confirmed"
              ? "bg-emerald-400/80"
              : s === "rac"
                ? "bg-amber-400/80"
                : s === "waiting"
                  ? "bg-rose-400/80"
                  : s === "cancelled"
                    ? "bg-zinc-600"
                    : "bg-zinc-800"
          }`}
        />
      ))}
    </div>
  );
};

const ProbabilityBar = ({ value }) => {
  const tone =
    value >= 70
      ? { stroke: "#34d399", glow: "rgba(52,211,153,.35)", label: "text-emerald-400" }
      : value >= 40
        ? { stroke: "#fbbf24", glow: "rgba(251,191,36,.35)", label: "text-amber-400" }
        : { stroke: "#fb7185", glow: "rgba(251,113,133,.35)", label: "text-rose-400" };
  return (
    <div className="flex items-center gap-3">
      <div className="flex-1 h-1.5 rounded-full bg-zinc-800/80 overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{
            width: `${value}%`,
            background: tone.stroke,
            boxShadow: `0 0 8px ${tone.glow}`,
          }}
        />
      </div>
      <span className={`text-[11.5px] font-semibold font-mono ${tone.label}`}>
        {value}%
      </span>
    </div>
  );
};

const PnrCard = ({
  pnr,
  index = 0,
  onRefresh,
  onDelete,
  onSyncCalendar,
  onAddToWallet,
  onAddToAppleWallet,
  onEditPassengers,
  onViewTrainStatus,
  onViewSeatChart,
  onShareTelegram,
  isRefreshing = false,
  isSyncing = false,
  isAddingWallet = false,
  walletEnabled = false,
  appleWalletEnabled = false,
}) => {
  const journeyDate = new Date(pnr.journey_date);
  const monthLabel = journeyDate
    .toLocaleDateString("en-IN", { month: "short" })
    .toUpperCase();
  const weekdayLabel = journeyDate
    .toLocaleDateString("en-IN", { weekday: "short" })
    .toUpperCase();
  const dayLabel = journeyDate.getDate();

  const statusBuckets = (pnr.passengers || []).reduce((acc, p) => {
    const s = railStatus(p.current_status);
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  const chartReady = pnr.chart_status === "CHART PREPARED";
  const probability = pnr.confirmation_probability ?? 0;

  return (
    <motion.article
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05, duration: 0.25 }}
      data-testid={`pnr-card-${pnr.pnr_number}`}
      className="group relative bg-zinc-900/40 border border-zinc-800/60 rounded-xl overflow-hidden hover:border-zinc-700 hover:bg-zinc-900/60 transition-all"
    >
      {/* LIVE ribbon */}
      {pnr.live && (
        <div className="absolute top-0 right-6 z-10 px-2 py-1 rounded-b-md bg-sky-500/15 border border-sky-500/30 border-t-0 flex items-center gap-1.5">
          <span className="relative inline-flex w-1.5 h-1.5">
            <span className="absolute inset-0 rounded-full bg-sky-400/60 animate-ping" />
            <span className="relative w-1.5 h-1.5 rounded-full bg-sky-400" />
          </span>
          <span className="text-[10px] font-semibold text-sky-300 tracking-wider font-mono">
            LIVE
          </span>
        </div>
      )}

      <div className="p-5">
        {/* Top row */}
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            {/* Date block */}
            <div className="shrink-0 w-14 text-center pt-1">
              <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 font-semibold font-mono">
                {monthLabel}
              </div>
              <div className="text-[26px] leading-none font-bold text-zinc-100 font-heading">
                {dayLabel}
              </div>
              <div className="text-[10px] text-zinc-500 mt-0.5 font-mono">
                {weekdayLabel}
              </div>
            </div>

            {/* perforation */}
            <div className="self-stretch w-px border-l border-dashed border-zinc-700/60 mx-1" />

            <div className="flex-1 min-w-0">
              {/* Train name + badges */}
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <h3 className="text-[16px] font-semibold text-zinc-50 whitespace-nowrap font-heading">
                  {pnr.train_name}
                </h3>
                <span className="px-1.5 py-0.5 rounded text-[10px] bg-zinc-800 border border-zinc-700 text-zinc-400 whitespace-nowrap font-mono">
                  {pnr.train_number}
                </span>
                {pnr.is_real_data === false && (
                  <span className="px-1.5 py-0.5 rounded text-[10px] bg-amber-500/10 text-amber-300 border border-amber-500/30 whitespace-nowrap font-mono">
                    DEMO
                  </span>
                )}
                {pnr.calendar_event_id && (
                  <span className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-emerald-500/10 text-emerald-300 border border-emerald-500/30 whitespace-nowrap font-mono">
                    <Check size={10} strokeWidth={2.4} /> CALENDAR
                  </span>
                )}
              </div>

              {/* Route ribbon */}
              <div className="flex items-center gap-3 text-[12.5px] text-zinc-300 mt-1.5 flex-wrap">
                <div className="flex items-center gap-1.5">
                  <span className="text-zinc-500 font-semibold text-[11px] font-mono">
                    {pnr.boarding_station}
                  </span>
                  <span className="text-zinc-100">
                    {pnr.boarding_station_name}
                  </span>
                  <span className="text-[11px] text-zinc-500 font-mono">
                    · {pnr.departure_time}
                  </span>
                </div>
                <div className="relative flex-1 min-w-[40px] max-w-[80px] h-px bg-gradient-to-r from-zinc-700 to-zinc-800">
                  <ArrowRight
                    size={12}
                    className="text-zinc-500 absolute -right-1 -top-[6px] bg-zinc-900/40"
                  />
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-zinc-500 font-semibold text-[11px] font-mono">
                    {pnr.destination_station}
                  </span>
                  <span className="text-zinc-100">
                    {pnr.destination_station_name}
                  </span>
                  <span className="text-[11px] text-zinc-500 font-mono">
                    · {pnr.arrival_time}
                  </span>
                </div>
              </div>

              {/* Meta strip */}
              <div className="mt-3 flex items-center gap-4 text-[11px] text-zinc-500 font-mono flex-wrap">
                <span className="flex items-center gap-1.5">
                  <span className="text-zinc-600">PNR</span>
                  <span className="text-zinc-300">{pnr.pnr_number}</span>
                </span>
                <span className="w-px h-3 bg-zinc-800" />
                <span className="flex items-center gap-1.5">
                  <span className="text-zinc-600">CLASS</span>
                  <span className="text-zinc-300">{pnr.journey_class}</span>
                </span>
                <span className="w-px h-3 bg-zinc-800" />
                <span
                  className={`flex items-center gap-1.5 ${
                    chartReady ? "text-emerald-400" : "text-zinc-400"
                  }`}
                >
                  <span
                    className={`w-1.5 h-1.5 rounded-full ${
                      chartReady ? "bg-emerald-400" : "bg-zinc-600"
                    }`}
                  />
                  {pnr.chart_status}
                </span>
              </div>
            </div>
          </div>

          {/* Action toolbar */}
          <div className="flex items-center gap-0.5 shrink-0 mt-1">
            <ActionBtn
              icon={<Radio size={14} />}
              label="Live status"
              tone="sky"
              testid={`live-status-${pnr.pnr_number}`}
              onClick={() => onViewTrainStatus?.(pnr)}
            />
            <ActionBtn
              icon={<Pencil size={14} />}
              label="Edit passengers"
              tone="emerald"
              testid={`edit-passengers-${pnr.pnr_number}`}
              onClick={() => onEditPassengers?.(pnr)}
            />
            <ActionBtn
              icon={<RefreshCw size={14} />}
              label="Refresh from IRCTC"
              tone="default"
              testid={`refresh-pnr-${pnr.pnr_number}`}
              onClick={() => onRefresh?.(pnr.id)}
              disabled={isRefreshing}
              spinning={isRefreshing}
            />
            <ActionBtn
              icon={<LayoutGrid size={14} />}
              label="Seat chart"
              tone="default"
              testid={`seat-chart-${pnr.pnr_number}`}
              onClick={() => onViewSeatChart?.(pnr)}
            />
            <ActionBtn
              icon={<Calendar size={14} />}
              label="Sync to Calendar"
              tone="indigo"
              testid={`sync-calendar-${pnr.pnr_number}`}
              onClick={() => onSyncCalendar?.(pnr.id)}
              disabled={isSyncing}
            />
            {walletEnabled && (
              <ActionBtn
                icon={<Wallet size={14} />}
                label="Add to Google Wallet"
                tone="emerald"
                testid={`add-wallet-${pnr.pnr_number}`}
                onClick={() => onAddToWallet?.(pnr)}
                disabled={isAddingWallet}
              />
            )}
            {appleWalletEnabled && (
              <ActionBtn
                icon={<Wallet size={14} />}
                label="Add to Apple Wallet"
                tone="default"
                testid={`add-apple-wallet-${pnr.pnr_number}`}
                onClick={() => onAddToAppleWallet?.(pnr)}
                disabled={isAddingWallet}
              />
            )}
            <ActionBtn
              icon={<Send size={14} />}
              label="Share on Telegram"
              tone="sky"
              testid={`share-telegram-${pnr.pnr_number}`}
              onClick={() => onShareTelegram?.(pnr)}
            />
            <span className="w-px h-5 bg-zinc-800 mx-1" />
            <ActionBtn
              icon={<Trash2 size={14} />}
              label="Delete"
              tone="rose"
              testid={`delete-pnr-${pnr.pnr_number}`}
              onClick={() => onDelete?.(pnr.id)}
            />
          </div>
        </div>

        {/* Passenger table */}
        <div className="bg-zinc-950/50 border border-zinc-800/60 rounded-lg overflow-hidden">
          <div className="grid grid-cols-[1fr_60px_120px_120px] items-center gap-3 px-3 py-2 border-b border-zinc-800/60 text-[9.5px] tracking-[0.18em] uppercase text-zinc-600 font-semibold font-mono">
            <span>Passenger</span>
            <span>Age/Sex</span>
            <span>Coach · Seat</span>
            <span className="text-right pr-1">Status</span>
          </div>
          {(pnr.passengers || []).map((p, pIndex) => {
            const seatPending =
              !p.seat_number || p.seat_number === "—" || p.seat_number === "";
            const initials = (p.name || "")
              .split(/\s+/)
              .map((n) => n[0])
              .filter(Boolean)
              .join("")
              .slice(0, 2)
              .toUpperCase();
            return (
              <div
                key={pIndex}
                data-testid={`passenger-${pnr.pnr_number}-${pIndex}`}
                className={`grid grid-cols-[1fr_60px_120px_120px] items-center gap-3 px-3 py-2.5 ${
                  pIndex < pnr.passengers.length - 1
                    ? "border-b border-zinc-800/40"
                    : ""
                } hover:bg-zinc-800/20`}
              >
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className="w-6 h-6 rounded-full bg-zinc-800 flex items-center justify-center text-[10px] text-zinc-400 font-semibold shrink-0">
                    {initials || "?"}
                  </div>
                  <span className="text-[13px] text-zinc-100 truncate">
                    {p.name}
                  </span>
                </div>
                <span className="text-[11.5px] text-zinc-500 font-mono">
                  {p.age}/{p.gender}
                </span>
                <span className="text-[11.5px] text-zinc-300 font-mono">
                  {seatPending ? (
                    <span className="text-zinc-600">— pending —</span>
                  ) : (
                    <>
                      <span className="text-zinc-500">{p.coach}</span>
                      {" · "}
                      <span className="text-zinc-100">{p.seat_number}</span>
                      {p.berth && p.berth !== "—" && (
                        <>
                          {" · "}
                          <span className="text-zinc-500">{p.berth}</span>
                        </>
                      )}
                    </>
                  )}
                </span>
                <div
                  className="text-right pr-1"
                  data-testid={`status-badge-${pnr.pnr_number}-${pIndex}`}
                >
                  <StatusPill status={p.current_status} />
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="mt-4 grid grid-cols-1 md:grid-cols-[1fr_auto_auto] items-center gap-4 md:gap-6">
          <div className="flex items-center gap-3">
            <span className="text-[10.5px] tracking-[0.18em] uppercase text-zinc-500 font-semibold whitespace-nowrap font-mono">
              Confirmation
            </span>
            <div className="flex-1 max-w-[260px]">
              <ProbabilityBar value={probability} />
            </div>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            {Object.entries(statusBuckets).map(([k, v]) => {
              const c = statusColor[k];
              return (
                <span
                  key={k}
                  className={`px-1.5 py-0.5 rounded text-[10px] border font-mono ${c.text} ${c.border} ${c.bg}`}
                >
                  {v}× {k.toUpperCase()}
                </span>
              );
            })}
          </div>
          <SeatMicrograph passengers={pnr.passengers || []} />
        </div>
      </div>
    </motion.article>
  );
};

export default PnrCard;
